// Thin wrapper around the practice_service API (docs/DECISIONS.md,
// "Self-practice: NCERT ingestion + server-side question bank"), mirroring
// studentProfile.js's apiFetch/origin-constant style.

import { apiFetch, PRACTICE_SERVICE_ORIGIN } from './api';

// Returns { grade, subject, lessons: [{ lesson_name, topics: [{ topic_name,
// sub_sub_topics: [...] }] }] } - a 3-level tree (chapter -> concept ->
// subconcept leaves), or null if the request failed or there's no content
// for this grade+subject pair.
export async function getSyllabusForGradeSubject(grade, subject) {
  const response = await apiFetch(
    `/api/practice/syllabus?grade=${encodeURIComponent(grade)}&subject=${encodeURIComponent(subject)}`,
    {},
    PRACTICE_SERVICE_ORIGIN
  );
  if (!response.ok) return null;
  return response.json();
}

// Replaces useCurriculumMenu.js's static GRADES array and
// subjectsForGrade.js's fixed grade->subject rule (docs/DECISIONS.md,
// "Admin CRUD on the self-practice syllabus hierarchy") - the menu's
// classes/subjects are now admin-editable Grade/Subject entities in
// PracticeSyllabusTable, not code-level constants, so an admin add/delete
// reaches students with no frontend deploy. Returns [] on any failure
// (matches getSyllabusForGradeSubject's null-on-failure convention, adapted
// to a list result).
export async function getGrades() {
  const response = await apiFetch('/api/practice/grades', {}, PRACTICE_SERVICE_ORIGIN);
  if (!response.ok) return [];
  return (await response.json()).grades;
}

export async function getSubjectsForGrade(grade) {
  const response = await apiFetch(
    `/api/practice/subjects?grade=${encodeURIComponent(grade)}`, {}, PRACTICE_SERVICE_ORIGIN
  );
  if (!response.ok) return [];
  return (await response.json()).subjects;
}

// Motivational quote shown while a question is generating (docs/
// DECISIONS.md, "Motivational quotes during question loading" decision) -
// replaces the static slide-image loader. Returns { text, author } or null
// (no quotes seeded yet, or the request failed) - Home.jsx falls back to a
// generic message in that case.
export async function getMotivationalQuote() {
  const response = await apiFetch('/api/practice/quote', {}, PRACTICE_SERVICE_ORIGIN);
  if (!response.ok) return null;
  return (await response.json()).quote;
}

// Admin-controlled feature visibility per class (docs/DECISIONS.md,
// "Admin-controlled feature visibility per class") - which
// StudentSidebar.jsx left-nav items are shown, resolved from the logged-in
// student's own grade. Returns { grade, visibility: { featureKey: bool } };
// visibility is only ever a partial map of explicitly-disabled features -
// callers must treat any key not present as visible (true) by default,
// never assume this object is exhaustive. Returns { grade: null,
// visibility: {} } on any failure, matching this file's "fail open, don't
// block the page" convention for every other getter here.
export async function getFeatureVisibility() {
  const response = await apiFetch('/api/practice/feature-visibility', {}, PRACTICE_SERVICE_ORIGIN);
  if (!response.ok) return { grade: null, visibility: {} };
  return response.json();
}

// Concept-completion email (docs/DECISIONS.md, "Concept-completion email"
// decision) - called once, client-side-guarded, when Home.jsx's
// isTopicComplete first goes true for a topic. The backend is itself
// idempotent (a conditional DynamoDB write), so a duplicate call here is
// harmless, but the caller still guards against firing it more than once
// per topic per session.
export async function reportConceptCompletion({
  topicKey, grade, subject, chapter, concept, subconcept, timeSpentSeconds, accuracy, questionsAttempted,
}) {
  return apiFetch(
    '/api/practice/concept-complete',
    {
      method: 'POST',
      body: JSON.stringify({
        topicKey, grade, subject, chapter, concept, subconcept,
        timeSpentSeconds, accuracy, questionsAttempted,
      }),
    },
    PRACTICE_SERVICE_ORIGIN
  );
}
